import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
import { Router } from '@angular/router';
import { DeviceService } from '../device/device.service';
import { HELP_CATEGORIES } from './help-center-data.const';
import { HelpArticle, HelpCategory } from './help-center.interface';

@Injectable({
  providedIn: 'root',
})
export class HelpCenterService {
  private router = inject(Router);
  private deviceService = inject(DeviceService);
  private translate = inject(TranslateService);
  private i18n = inject(I18nService);
  private readonly traducaoPronta = signal(0);

  constructor() {
    this.translate.onLangChange.subscribe(() => {
      this.traducaoPronta.update((versao) => versao + 1);
    });
    this.translate.onTranslationChange.subscribe(() => {
      this.traducaoPronta.update((versao) => versao + 1);
    });
  }

  private readonly _isOpen = signal(false);
  private readonly _activeArticleId = signal<string | null>(null);
  private readonly _searchQuery = signal('');
  private readonly _expandedCategories = signal<Record<string, boolean>>({
    introducao: true,
    configuracoes: true,
    'uso-diario': true,
    faq: true,
  });

  /** Signals públicos somente leitura */
  readonly $isOpen = this._isOpen.asReadonly();
  readonly $searchQuery = this._searchQuery.asReadonly();
  readonly $expandedCategories = this._expandedCategories.asReadonly();
  readonly $isMobile = this.deviceService.$isMobile;

  readonly $categories = computed<HelpCategory[]>(() => {
    this.i18n.idioma();
    this.traducaoPronta();
    return HELP_CATEGORIES.map((categoria) => ({
      id: categoria.id,
      title: this.traduzir(categoria.titleKey),
      icon: categoria.icon,
      articles: categoria.articles.map((artigo) => this.traduzirArtigo(artigo)),
    }));
  });

  readonly $activeArticle = computed<HelpArticle | null>(() => {
    const id = this._activeArticleId();
    if (!id) {
      return null;
    }
    return this.findArticleById(id) ?? null;
  });

  /** Lista filtrada de categorias e artigos conforme a busca */
  readonly $filteredCategories = computed<HelpCategory[]>(() => {
    const categorias = this.$categories();
    const query = this._searchQuery().trim().toLowerCase();
    if (!query) {
      return categorias;
    }

    return categorias
      .map((cat) => {
        const fonte = HELP_CATEGORIES.find((item) => item.id === cat.id);
        const filteredArticles = cat.articles.filter((article) => {
          const keywords = fonte?.articles.find((item) => item.id === article.id)?.keywords ?? [];
          const inTitle = article.title.toLowerCase().includes(query);
          const inSummary = article.summary.toLowerCase().includes(query);
          const inKeywords = keywords.some((k) => k.toLowerCase().includes(query));
          return inTitle || inSummary || inKeywords;
        });

        return {
          ...cat,
          articles: filteredArticles,
        };
      })
      .filter((cat) => cat.articles.length > 0);
  });

  /**
   * Abre a Central de Ajuda.
   * Se um `articleId` for informado, abre diretamente esse artigo.
   * Se não for informado, tenta abrir o artigo contextual da rota atual (ex: /agenda -> gestao-agenda).
   */
  openHelp(articleId?: string): void {
    if (articleId) {
      const found = this.findArticleById(articleId);
      this._activeArticleId.set(found ? articleId : null);
    } else {
      const routeArticle = this.getArticleByCurrentRoute();
      this._activeArticleId.set(routeArticle?.id ?? null);
    }

    this._isOpen.set(true);
  }

  /** Fecha o drawer/modal da Central de Ajuda */
  closeHelp(): void {
    this._isOpen.set(false);
  }

  /** Seleciona um artigo para visualização */
  selectArticle(article: HelpArticle | null): void {
    this._activeArticleId.set(article?.id ?? null);
  }

  /** Atualiza o termo de busca */
  setSearchQuery(query: string): void {
    this._searchQuery.set(query);
  }

  /** Alterna a expansão de uma categoria no accordion */
  toggleCategory(categoryId: string): void {
    this._expandedCategories.update((curr) => ({
      ...curr,
      [categoryId]: !curr[categoryId],
    }));
  }

  /** Busca artigo por ID no idioma atual */
  findArticleById(articleId: string): HelpArticle | undefined {
    for (const cat of this.$categories()) {
      const found = cat.articles.find((a) => a.id === articleId);
      if (found) return found;
    }
    return undefined;
  }

  private traduzir(chave: string): string {
    const valor = this.translate.instant(chave);
    return valor && valor !== chave ? valor : chave;
  }

  private traduzirArtigo(artigo: (typeof HELP_CATEGORIES)[number]['articles'][number]): HelpArticle {
    return {
      id: artigo.id,
      title: this.traduzir(artigo.titleKey),
      summary: this.traduzir(artigo.summaryKey),
      category: this.traduzir(artigo.categoryKey),
      icon: artigo.icon,
      keywords: artigo.keywords,
      contentHtml: this.traduzir(artigo.contentKey),
      routeContext: artigo.routeContext,
    };
  }

  /** Identifica o artigo mais relevante para a rota atual */
  private getArticleByCurrentRoute(): HelpArticle | undefined {
    const currentUrl = this.router.url;

    if (currentUrl.includes('/agenda')) {
      return this.findArticleById('gestao-agenda');
    }
    if (currentUrl.includes('/estabelecimentos')) {
      return this.findArticleById('dados-estabelecimento');
    }
    if (currentUrl.includes('/dashboard')) {
      return this.findArticleById('dashboard-financeiro');
    }
    if (currentUrl.includes('/primeiro-acesso')) {
      return this.findArticleById('primeiro-acesso');
    }
    if (currentUrl.includes('/login')) {
      return this.findArticleById('cadastro-e-login');
    }

    return undefined;
  }
}
